import { useRecruitmentApi } from 'infrastructure/api';
import { ResourceData, ResourceId, useCrudService, useTimelineService } from 'framework/crud';
import { DESC } from 'framework/list';
import { FormFieldsData } from 'framework/form';
import { cloneDeep, get, isEqual, set } from 'lodash';
import { ADDITIONAL_CONTACTS_OPTIONS } from 'domain/shared/contact';
import { CandidateApplication, CandidateApplicationsResponse, CandidateStatusCommentMetaFields } from './types';
import { Stages } from './applicationPipeline';
import { useVacancyGroupService, VacancyGroupType } from 'domain/shared/newVacancy';
import { ErrorMessage, useMessageService } from 'framework/ui/Message';
import { CANDIDATES_ROUTE } from './model';
import { useCandidateUrl } from './route/hooks/useCandidateUrl';
import candidateValidation from './validation/candidateValidation';
import candidateValidationInlineVersion from './validation/candidateValidationInlineVersion';
import useCandidateApplicationPipelineService from './useCandidateApplicationPipelineService';

export type CandidateDuplicate = {
  url: string;
  candidate: ResourceData;
  // TODO: need add types for LinkedIn, Skype, Telegram, Whatsapp and Viber after implementing them on BE
  score: {
    name: number;
    personalPhoneNumber: number;
    personalEmail: number;
    otherEmail: number;
    additionalContacts?: {
      LinkedIn?: number;
    };
    total?: number;
  };
};

export const CANDIDATE_RESOURCE_URL = '/candidates';

const BLACKLIST_FILTER_KEY = 'filter.isInBlackList.eq';
const POTENTIAL_CANDIDATE_FILTER_KEY = 'filter.isMarkedAsPotentialCandidate.eq';

export function convertTelegramLink(input: string): string {
  const telegramUrlPattern = /^(https?:\/\/)?(www\.)?t\.me\/[a-z0-9_]+$/i;
  const simplifiedUrlPattern = /^t\.me\/[a-z0-9_]+$/i;

  if (telegramUrlPattern.test(input) || simplifiedUrlPattern.test(input)) {
    return input.toLowerCase().startsWith('http') ? input : `https://${input}`;
  }

  if (/^@[a-z0-9_]+$/i.test(input)) {
    const username = input.substring(1);
    return `https://t.me/${username.toLowerCase()}`;
  }

  return input;
}

/**
 * Candidate service.
 */
const useCandidateService = () => {
  const { getProfileUrl } = useCandidateUrl();
  const { sendGetRequest, sendPostRequest, sendPutRequest } = useRecruitmentApi();
  const { moveApplicationOfCandidate } = useCandidateApplicationPipelineService();
  const { search: searchVacancyGroup } = useVacancyGroupService();
  const { addMessage } = useMessageService();

  const getGlobalRecruitingPermissions = async (): Promise<string[]> => {
    try {
      const response = await sendGetRequest(`/permissions`);
      return await response.json();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('getGlobalRecruitingPermissions: Failed to fetch global permissions:', error);
      return [];
    }
  };

  const getResourceBasedRecruitingPermissions = async (id: ResourceId) => {
    try {
      const response = await sendGetRequest(`/permissions/Candidate/${id}`);
      return response.json();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('getResourceBasedRecruitingPermissions: Failed to fetch global permissions:', error);
      return [];
    }
  };

  const getResourceBasedRecruitingPermissionsDuplicates = async (id: ResourceId) => {
    try {
      const response = await sendGetRequest(`/permissions/CandidateDuplicateScanResult/${id}`);
      return response.json();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('getResourceBasedRecruitingPermissionsDuplicates: Failed to fetch global permissions:', error);
      return [];
    }
  };

  const getCombinedPermissions = async (id: ResourceId): Promise<string[]> => {
    try {
      const results = await Promise.allSettled([
        getGlobalRecruitingPermissions(),
        getResourceBasedRecruitingPermissions(id),
      ]);
      const permissions = results.flatMap(result =>
        result.status === 'fulfilled' && Array.isArray(result.value) ? result.value : [],
      );

      return permissions;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('getCombinedPermissions: Error while combining permissions:', error);
      return [];
    }
  };

  const getCombinedPermissionsDuplicates = async (id: ResourceId): Promise<string[]> => {
    try {
      const results = await Promise.allSettled([
        getGlobalRecruitingPermissions(),
        getResourceBasedRecruitingPermissionsDuplicates(id),
      ]);
      const permissions = results.flatMap(result =>
        result.status === 'fulfilled' && Array.isArray(result.value) ? result.value : [],
      );

      return permissions;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('getCombinedPermissionsDuplicates: Error while combining permissions:', error);
      return [];
    }
  };

  const {
    search: baseTimelineSearch,
    create: createTimelineItem,
    getTimelinePageById,
  } = useTimelineService({
    parentEntityUrl: `/candidates/:id`,
    useService: useRecruitmentApi,
  });

  const searchTimelineItems = async (candidateId: ResourceId, request: any) => {
    return baseTimelineSearch(candidateId, {
      ...request,
      sort: {
        ...request.sort,
        id: DESC,
      },
    });
  };

  const { search: baseSearch, ...baseCrudRequests } = useCrudService({
    singleResourceUrl: '/candidates/:id',
    listResourceUrl: CANDIDATE_RESOURCE_URL,
    useService: useRecruitmentApi,
  });

  const search = (request: any) => {
    const blackListFilterValue = get(request, BLACKLIST_FILTER_KEY);
    let blackListFilter = null;

    if (blackListFilterValue === 'YES') {
      blackListFilter = true;
    } else if (blackListFilterValue === 'NO') {
      blackListFilter = false;
    }

    const potentialCandidateFilterValue = get(request, POTENTIAL_CANDIDATE_FILTER_KEY);
    let potentialCandidateFilter = null;

    if (potentialCandidateFilterValue === 'YES') {
      potentialCandidateFilter = true;
    } else if (potentialCandidateFilterValue === 'NO') {
      potentialCandidateFilter = false;
    }

    const params = cloneDeep(request);

    if (blackListFilter !== null) {
      set(params, BLACKLIST_FILTER_KEY, blackListFilter);
    }

    if (potentialCandidateFilter !== null) {
      set(params, POTENTIAL_CANDIDATE_FILTER_KEY, potentialCandidateFilter);
    }

    return baseSearch(params);
  };

  const visit = async (id: ResourceId) => {
    const url = `/candidates/${id}/visit`;
    return sendPostRequest(url, id);
  };

  const getValidationSchema = () => Promise.resolve(candidateValidation);
  const getInlineValidationSchema = () => Promise.resolve(candidateValidationInlineVersion);

  const getDuplicates = async (data: FormFieldsData) => {
    const response = await sendPostRequest(`/candidates/matches`, data);
    const duplicates = (await response.json()).result || [];

    return duplicates.map((duplicate: CandidateDuplicate) => ({
      ...duplicate,
      url: getProfileUrl(duplicate.candidate.id),
    }));
  };

  const canSearchDuplicates = (data: FormFieldsData) => {
    return data?.name?.firstName && data?.name?.lastName;
  };

  const createCandidateAndLeftNote = async (formData: any) => {
    const { mainData, ...additionalData } = formData;
    const { candidateId } = mainData;
    const { timelineNote, resume } = additionalData;

    if (candidateId) {
      let response;

      if (timelineNote?.noteText) {
        response = await createTimelineItem(candidateId, 'notes', { ...timelineNote });
      }

      if (resume?.resumeArtifactIds?.length) {
        response = await createTimelineItem(candidateId, 'resumes', { ...resume });
      }

      return {
        ...response,
        isUpdate: true,
        candidateId,
      };
    }

    const { create: createNewCandidate } = baseCrudRequests;
    const newCandidateResponse = await createNewCandidate(additionalData);
    const newCandidateId = newCandidateResponse.id;

    if (timelineNote) {
      await createTimelineItem(newCandidateId, 'notes', { ...timelineNote });
    }

    return newCandidateResponse;
  };

  const sendSubmissionDuplicateReport = async (data: any) => {
    const duplicates = data.foundDuplicates;
    let result = [];
    if (duplicates && duplicates.length > 0 && duplicates[0].requestId) {
      const request = {
        requestId: duplicates[0].requestId,
        chosenCandidateId: data.candidateId,
        matches: duplicates.map((duplicateRecord: any) => {
          return {
            candidateId: duplicateRecord.candidate.id,
            score: duplicateRecord.score.total,
          };
        }),
      };
      const response = await sendPostRequest('/candidates/metrics/search-duplicate/submit-action', request);
      result = await response.json();
    }
    return result;
  };

  const reportWrongDuplicateResult = async (data: any) => {
    const request = {
      candidateId: data.candidate.id,
      requestId: data.requestId,
      matchScore: data.score.total,
    };
    const response = await sendPostRequest('/candidates/metrics/search-duplicate/wrong-report', request);
    const json = await response.json();
    return json;
  };

  const confirmCandidateSpecialization = async (
    candidateId: StringOrNumber,
    specialization: string,
    values: FormFieldsData,
  ) => {
    const res = await sendPostRequest(`/candidates/${candidateId}/confirm-specialization`, {
      specialization,
      ...values,
    });

    return res.json();
  };

  const updateCandidateProfile = async (id: StringOrNumber, values: FormFieldsData) => {
    try {
      const {
        statusChangeComment,
        initialIsMarkedAsPotentialCandidate,
        initialIsInBlackList,
        initialIsReferral,
        ...profileValues
      } = values as FormFieldsData & CandidateStatusCommentMetaFields;
      const nameParts = values.name.fullName.split(' ');
      const firstNamePart = nameParts[0];
      const lastNamePart = [...nameParts.slice(1)].join(' ');

      const { additionalContacts } = profileValues;
      const putObject = {
        ...profileValues,
        name: {
          ...profileValues.name,
          firstName: firstNamePart,
          lastName: lastNamePart,
        },
        additionalContacts: additionalContacts
          .filter((item: any) => item.value)
          .map((el: any) => {
            if (el.type === ADDITIONAL_CONTACTS_OPTIONS.Telegram) {
              return { ...el, value: convertTelegramLink(el.value) };
            }
            return el;
          }),
      };

      if (
        values.lastUploadedResumeIds &&
        (!values.artifacts || !isEqual(values.lastUploadedResumeIds, values.artifacts))
      ) {
        const resumeObj = {
          resumeArtifactIds: values.lastUploadedResumeIds,
          resumeType: 'STAND_ALONE',
        };
        try {
          await sendPostRequest(`${CANDIDATES_ROUTE}/${id}/resumes`, resumeObj);
        } catch (error) {
          throw new Error('Resume upload failed');
        }
      }

      const res = await sendPutRequest(`${CANDIDATES_ROUTE}/${id}`, putObject);

      return res.json();
    } catch (error) {
      throw new Error('Error with updating candidate profile');
    }
  };

  const getCandidateApplications = async (candidateId: StringOrNumber) => {
    try {
      const url = `/candidates/${candidateId}/applications/search`;
      const response = await sendPostRequest(url, {
        pageSize: 200,
        sort: {
          createdAt: 'DESC',
        },
      });

      return (await response.json()) as CandidateApplicationsResponse;
    } catch (error) {
      return false;
    }
  };

  const changeCandidateApplicationPiplineStep = async (
    candidateApplicationId: StringOrNumber,
    moveToStageName: Stages,
    payload: any = null,
  ) => {
    if (payload?.resumeId?.length) {
      // workaround to correctly pass single artifact
      // eslint-disable-next-line no-param-reassign, prefer-destructuring
      payload.resumeId = payload.resumeId[0];
    }
    if (payload?.screeningMaterialsId) {
      // same as above (workaround to correctly pass single artifact)
      // eslint-disable-next-line no-param-reassign, prefer-destructuring
      payload.screeningMaterialsId = payload.screeningMaterialsId[0];
    }
    if (candidateApplicationId) {
      const res = await moveApplicationOfCandidate(candidateApplicationId, moveToStageName, payload);
      if (res === false) {
        return false;
      }
      return true;
    } else {
      return false;
    }
  };

  const getCompleteCandidate = async (id: ResourceId) => {
    try {
      // 1. Fetch basic candidate data
      const candidateData = await baseCrudRequests.getById(id);
      // 2. Fetch all applications for this candidate
      const applicationsResponse = await getCandidateApplications(id);
      const applications = applicationsResponse || { result: [] };
      const lastActiveApplication = applications.result.find(
        (app: CandidateApplication) => !['Rejected', 'Closed'].includes(app.applicationPipelineState.stage),
      );

      // Create an array of all other applications (previous applications)
      const previousApplications = lastActiveApplication
        ? applications.result.filter(app => app.id !== lastActiveApplication.id)
        : applications.result;

      // 3. Extract all vacancy group aliases that need to be fetched
      const vacancyGroupAliases = applications.result.map(app => app.vacancyGroupAlias).filter(Boolean);

      // 4. Add the last applied vacancy alias if it exists and isn't already included
      const { lastAppliedVacancy } = candidateData;
      if (lastAppliedVacancy && !vacancyGroupAliases.includes(lastAppliedVacancy)) {
        vacancyGroupAliases.push(lastAppliedVacancy);
      }

      // 5. Fetch all vacancy groups in a single request
      let vacancyGroups: Record<string, VacancyGroupType> = {};
      let lastAppliedVacancyData: VacancyGroupType | null = null;

      if (vacancyGroupAliases.length > 0) {
        const vacancyGroupsResponse = await searchVacancyGroup({
          filter: {
            alias: { in: vacancyGroupAliases },
          },
          sort: { createdAt: 'DESC' },
          pageSize: vacancyGroupAliases.length,
        });

        // 6. Transform vacancy groups into a map for easier access
        vacancyGroups = Object.fromEntries(
          (vacancyGroupsResponse.result || []).map((group: VacancyGroupType) => [group.alias, group]),
        );

        if (lastAppliedVacancy && vacancyGroups[lastAppliedVacancy]) {
          lastAppliedVacancyData = vacancyGroups[lastAppliedVacancy];
        }
      }

      // 7. Combine all data
      return {
        ...candidateData,
        statusChangeComment: '',
        initialIsMarkedAsPotentialCandidate: candidateData.isMarkedAsPotentialCandidate,
        initialIsInBlackList: candidateData.isInBlackList,
        initialIsReferral: candidateData.isReferral,
        applications,
        lastActiveApplication,
        previousApplications,
        vacancyGroups,
        lastAppliedVacancyData,
      };
    } catch (error) {
      addMessage(new ErrorMessage('Error fetching complete candidate data'));
      throw error;
    }
  };

  return {
    search,
    visit,
    getValidationSchema,
    searchTimelineItems,
    createTimelineItem,
    getTimelinePageById,
    ...baseCrudRequests,
    getDuplicates,
    canSearchDuplicates,
    createCandidateAndLeftNote,
    sendSubmissionDuplicateReport,
    reportWrongDuplicateResult,
    confirmCandidateSpecialization,
    updateCandidateProfile,
    getInlineValidationSchema,
    changeCandidateApplicationPiplineStep,
    getCandidateApplications,
    getCompleteCandidate,
    getResourceBasedRecruitingPermissions,
    getResourceBasedRecruitingPermissionsDuplicates,
    getGlobalRecruitingPermissions,
    getCombinedPermissions,
    getCombinedPermissionsDuplicates,
  };
};

export default useCandidateService;
